import React from 'react';
import { View } from 'react-native';

import { IconLocationFill, Text, createMqStyles, tokens } from '@fhs/ui';

import { useEmptyStateMqStyles } from '../empty-state/empty-state.styles';

import { SavedAddressesSectionProps } from './types';

/** The design draws every empty-state glyph as an 80px icon tinted to 10% black. */
const ICON_SIZE = 80;

const EMPTY_STATE_TITLE = 'No Saved Addresses';
const SIGNED_OUT_BODY = 'Your saved addresses will appear here when you log in or sign up.';
const NO_ADDRESSES_BODY =
  'Order from a new delivery address and save it at checkout. It will appear here the next time you order.';
export const SavedAddressesSection: React.VFC<SavedAddressesSectionProps> = ({
  isAuthenticated,
}) => {
  const styles = useSavedAddressesSectionMqStyles();

  return (
    <View testID="store-locator-delivery-saved-addresses">
      {/*
       * Styled as the active tab of the pickup locator's tab bar so the two panels line up, but
       * there is only ever one section here, so it is a heading rather than an interactive tab.
       */}
      <View style={styles.header}>
        <View style={styles.headerTab}>
          <Text.Ui
            size="md"
            weight="semibold"
            accessibilityRole="header"
            style={styles.headerLabel}
          >
            Saved Addresses
          </Text.Ui>
        </View>
      </View>

      <SavedAddressesEmptyState body={isAuthenticated ? NO_ADDRESSES_BODY : SIGNED_OUT_BODY} />
    </View>
  );
};

const SavedAddressesEmptyState: React.VFC<{ body: string }> = ({ body }) => {
  const styles = useEmptyStateMqStyles();
  const sectionStyles = useSavedAddressesSectionMqStyles();

  return (
    <View
      style={styles.wrapper}
      accessibilityLabel={`${EMPTY_STATE_TITLE}. ${body}`}
      testID="store-locator-delivery-saved-addresses-empty-state"
    >
      <IconLocationFill size={ICON_SIZE} color={tokens.colors.$blackOpacity10} />

      <Text.Heading type="two" style={styles.title}>
        {EMPTY_STATE_TITLE}
      </Text.Heading>

      <Text.Paragraph size="md" style={[styles.body, sectionStyles.emptyStateBody]}>
        {body}
      </Text.Paragraph>
    </View>
  );
};

const useSavedAddressesSectionMqStyles = createMqStyles({
  header: {
    $base: {
      paddingTop: 16,
    },
  },
  headerTab: {
    $base: {
      alignItems: 'center',
      borderBottomColor: tokens.colors.$houseRed,
      borderBottomWidth: 2,
      paddingBottom: 12,
      paddingHorizontal: 16,
    },
  },
  headerLabel: {
    $base: {
      color: tokens.colors.$houseRed,
    },
  },
  // Unlike the pickup empty states, the delivery design sets this copy in full house dark.
  emptyStateBody: {
    $base: {
      color: tokens.colors.$houseDark,
    },
  },
});
